import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ALL_PERMISSIONS, CONTAINER_PERMISSIONS, AclEntry, Container, Explanation, type Permission, type Principal, type WorkObject } from "@workcore/schemas";
import { api, get } from "../../api";
import { KIND_LABEL, parsePrincipal, useDirectory, useRoles } from "../../directory";
import { Breadcrumbs, ErrorBox, PageHeader, Panel } from "../../ui";
import { useDefinition } from "../editor/shared";
import { ObjectTree, treeKey } from "./tree";
import { useMe } from "./Projects";
import { confirmImpact } from "../../confirm";

const PERM_LABEL: Record<Permission, string> = { read: "read", comment: "comment", update: "update", create_child: "create child", delete: "delete", manage_acl: "manage access", view_costs: "view costs", manage_costs: "manage costs" };
type Scope = { kind: "container" | "object"; id: number };

/** Dry run, then ask (when there is something to say), then commit. Returns false if cancelled. */
async function previewed<T>(method: string, path: string, body: unknown, describe?: (dry: T) => string | undefined): Promise<T | false> {
  const dry = await api<T>(method, path, { body, dryRun: true });
  const msg = describe?.(dry);
  if (msg && !(await confirmImpact(msg))) return false;
  return api<T>(method, path, { body });
}

/** One scope's grants as a grid: a row per principal, a checkbox per permission. */
function Grants({ slug, scope, title, note }: { slug: string; scope: Scope; title: string; note?: string }) {
  const dir = useDirectory(slug);
  const q = useQueryClient();
  const key = ["ws", slug, "acl", scope.kind, scope.id];
  const acl = useQuery({ queryKey: key, queryFn: async () => z.array(AclEntry).parse(await get(`/v1/ws/${slug}/acl/${scope.kind}/${scope.id}`)) });
  const [who, setWho] = useState("");
  const path = `/v1/ws/${slug}/acl/${scope.kind}/${scope.id}`;
  const done = () => q.invalidateQueries({ queryKey: ["ws", slug] });
  const set = useMutation({
    mutationFn: ({ principal, permissions }: { principal: Principal; permissions: Permission[] }) =>
      permissions.length === 0
        ? previewed("DELETE", path, { principal }, () => `Remove every permission ${dir.label(principal)} has here.`)
        : previewed("PUT", path, { principal, permissions }),
    onSettled: done,
  });
  const rows = acl.data ?? [];
  // a project's money is granted on the project only, never on an item in it
  const PERMS = scope.kind === "container" ? CONTAINER_PERMISSIONS : ALL_PERMISSIONS;
  const taken = new Set(rows.map((e) => `${e.principal.kind}:${e.principal.id}`));
  const toggle = (e: AclEntry, p: Permission) => {
    const has = e.permissions.includes(p);
    // read is the base of everything else: unticking it clears the row; ticking anything else adds it
    let next = has ? e.permissions.filter((x) => x !== p) : [...e.permissions, p];
    if (p === "read" && has) next = [];
    if (!has && p !== "read" && !next.includes("read")) next = ["read", ...next];
    set.mutate({ principal: e.principal, permissions: PERMS.filter((x) => next.includes(x)) });
  };
  return (
    <Panel title={title}>
      {note && <p className="muted small">{note}</p>}
      <ErrorBox error={acl.error ?? set.error} />
      <table className="grid perms">
        <thead>
          <tr>
            <th>Who</th>
            {PERMS.map((p) => <th key={p}>{PERM_LABEL[p]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id}>
              <td>{dir.label(e.principal)} <span className="muted small">{KIND_LABEL[e.principal.kind]}</span></td>
              {PERMS.map((p) => (
                <td key={p}>
                  <input type="checkbox" aria-label={`${dir.label(e.principal)} ${PERM_LABEL[p]}`} checked={e.permissions.includes(p)} onChange={() => toggle(e, p)} />
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && !acl.isLoading && (
            <tr><td colSpan={PERMS.length + 1} className="muted">No grants here.</td></tr>
          )}
        </tbody>
      </table>
      <form autoComplete="off"
        className="inline"
        onSubmit={(e) => {
          e.preventDefault();
          if (who) set.mutate({ principal: parsePrincipal(who), permissions: ["read", "comment"] }, { onSuccess: () => setWho("") });
        }}
      >
        <label className="inline-field"><span>Give access to</span><select aria-label={`Grant on ${title}`} value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">Give someone access…</option>
          {dir.options.filter((o) => !taken.has(o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select></label>
        <button type="submit" disabled={!who}>Add (read + comment)</button>
      </form>
    </Panel>
  );
}

function NodePanel({ slug, node, onChanged }: { slug: string; node: WorkObject; onChanged: () => void }) {
  const dir = useDirectory(slug);
  const me = useMe(slug).data;
  const [keep, setKeep] = useState<string[]>([]);
  const restrict = useMutation({
    mutationFn: () => {
      const principals = keep.map(parsePrincipal);
      return previewed<{ kept: AclEntry[] }>("POST", `/v1/ws/${slug}/objects/${node.id}/restrict`, { keep: principals }, (dry) =>
        `Restrict '${node.name}' and everything under it. Inheritance stops here: only these keep access, with the permissions they have now:\n` +
        dry.kept.map((e) => `  • ${dir.label(e.principal)}: ${e.permissions.join(", ")}`).join("\n") +
        "\nProject admins always see everything.",
      );
    },
    onSettled: onChanged,
  });
  const unrestrict = useMutation({
    mutationFn: () =>
      previewed<{ nowVisibleTo: Principal[] }>("POST", `/v1/ws/${slug}/objects/${node.id}/unrestrict`, undefined, (dry) =>
        dry.nowVisibleTo.length === 0
          ? `Unrestrict '${node.name}'. Nobody new gains access.`
          : `Unrestrict '${node.name}'. These gain access to it and everything under it:\n${dry.nowVisibleTo.map((p) => `  • ${dir.label(p)}`).join("\n")}`,
      ),
    onSettled: onChanged,
  });
  const meKey = me?.user ? `user:${me.user.id}` : "";
  return (
    <>
      <Panel title={`#${node.id} ${node.name}`}>
        {node.restricted ? (
          <>
            <p>Restricted: grants inherited from above stop here. Only grants on this node (and below) apply.</p>
            <button className="secondary" onClick={() => unrestrict.mutate()}>Unrestrict…</button>
          </>
        ) : (
          <>
            <p className="muted">Inherits access from above. Restrict it to stop inheritance here; choose who keeps their current access (you always do).</p>
            <div className="keep">
              {dir.options.filter((o) => o.value !== meKey).map((o) => (
                <label key={o.value} className="check">
                  <input type="checkbox" checked={keep.includes(o.value)} onChange={(e) => setKeep(e.target.checked ? [...keep, o.value] : keep.filter((x) => x !== o.value))} /> {o.label}
                </label>
              ))}
            </div>
            <button onClick={() => restrict.mutate()}>Restrict…</button>
          </>
        )}
        <ErrorBox error={restrict.error ?? unrestrict.error} />
      </Panel>
      <Grants slug={slug} scope={{ kind: "object", id: node.id }} title="Grants on this node" note="Apply to this node and everything under it." />
      <Explain slug={slug} node={node} />
    </>
  );
}

function Explain({ slug, node }: { slug: string; node: WorkObject }) {
  const dir = useDirectory(slug);
  const [who, setWho] = useState("");
  const ex = useQuery({
    queryKey: ["ws", slug, "explain", node.id, who],
    enabled: who !== "",
    queryFn: async () => Explanation.parse(await get(`/v1/ws/${slug}/objects/${node.id}/explain`, { principal: who })),
  });
  const e = ex.data;
  const scopeName = (s: { kind: string; id: number }) => (s.kind === "container" ? "the project" : `#${s.id}`);
  return (
    <Panel title="Why can they see it?">
      <label className="inline-field"><span>Explain for</span><select aria-label="Explain for" value={who} onChange={(ev) => setWho(ev.target.value)}>
        <option value="">Choose someone…</option>
        {dir.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select></label>
      <ErrorBox error={ex.error} />
      {e && (
        <div className="explain">
          <p>
            <strong>{dir.label(e.principal)}</strong>{" "}
            {e.permissions.length === 0 ? <span className="err">cannot see this node.</span> : <>can <strong>{e.permissions.map((p) => PERM_LABEL[p]).join(", ")}</strong>.</>}
          </p>
          {e.isProjectAdmin && <p>They are a project admin of this project, which gives everything regardless of grants.</p>}
          <p className="muted small">Their role allows at most: {e.roleCeiling.map((p) => PERM_LABEL[p]).join(", ") || "nothing"}. Acting as: {e.principals.map((p) => dir.label(p)).join(", ")}.</p>
          {e.grants.length > 0 && (
            <table className="grid">
              <thead><tr><th>Grant on</th><th>Through</th><th>Permissions</th><th>Applies?</th></tr></thead>
              <tbody>
                {e.grants.map((g) => (
                  <tr key={g.entryId} className={g.applies ? "" : "inactive"}>
                    <td>{scopeName(g.scope)}</td>
                    <td>{dir.label(g.via)}</td>
                    <td>{g.permissions.map((p) => PERM_LABEL[p]).join(", ")}</td>
                    <td>{g.applies ? "yes" : "no: cut off by a restricted node"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {e.grants.length === 0 && !e.isProjectAdmin && <p className="muted">No grant reaches this node for them or any group they are in.</p>}
        </div>
      )}
    </Panel>
  );
}

export function AccessPage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const q = useQueryClient();
  const container = useQuery({ queryKey: ["ws", slug, "container", id], queryFn: async () => Container.parse(await get(`/v1/ws/${slug}/containers/${id}`)) });
  const def = useDefinition(slug, container.data?.definitionId ?? 0);
  const roles = useRoles(slug).data ?? [];
  const dir = useDirectory(slug);
  const me = useMe(slug).data;
  const [node, setNode] = useState<WorkObject>();
  const admins = roles.filter((r) => r.role === "project_admin" && r.scopeId === id);
  const isPA = me?.roles.some((r) => r.role === "project_admin" && r.scopeId === id) ?? false;
  const refreshTree = () => {
    void q.invalidateQueries({ queryKey: treeKey(slug, id) });
    void q.invalidateQueries({ queryKey: ["ws", slug, "acl"] });
    setNode(undefined);
  };
  const c = container.data;
  return (
    <>
      <ErrorBox error={container.error} />
      {c && (
        <>
          <PageHeader
            back={<Breadcrumbs items={[{ label: "Projects", to: `/w/${slug}/projects` }, { label: c.name }, { label: "Access" }]} />}
            title={`${c.name}: access`}
            description={`Project admins (${admins.map((a) => dir.label(a.principal)).join(", ") || "none"}) see everything here. Everyone else needs a grant on the project or on a node above what they want to see; a restricted node stops grants from above.`}
          />
          {!isPA && <p className="error">You are not a project admin here, so you can only see what your own grants allow. A system admin can grant themselves project admin on the Roles screen; that is recorded.</p>}
          <Grants slug={slug} scope={{ kind: "container", id }} title="Grants on the whole project" />
          <div className="split">
            <Panel title="Structure">
              <p className="muted small">Names and types only. Select a node to manage its access.</p>
              {def.data && <ObjectTree slug={slug} containerId={id} def={def.data} selectedId={node?.id} onSelect={setNode} />}
            </Panel>
            <div>{node ? <NodePanel key={node.id} slug={slug} node={node} onChanged={refreshTree} /> : <p className="muted pad">Select a node in the structure.</p>}</div>
          </div>
        </>
      )}
    </>
  );
}
